import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'
import { httpsCallable } from 'firebase/functions'
import { toast } from 'sonner'
import { app, functions, isDevMode } from './firebase'

const registerFcmTokenFn = httpsCallable<{ token: string }, { success: boolean }>(
  functions, 'registerFcmToken'
)

async function getAndRegisterToken(): Promise<void> {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined
  if (!vapidKey) return
  const supported = await isSupported()
  if (!supported) return
  try {
    const messaging = getMessaging(app)
    const token = await getToken(messaging, { vapidKey })
    if (token) await registerFcmTokenFn({ token })
  } catch {
    // Silently fail — notifications are non-critical
  }
}

// Called on auth state change. Only registers if permission already granted.
// Does NOT prompt — Chrome 80+ blocks requestPermission() from non-user-gestures.
export async function initFcm(): Promise<void> {
  if (isDevMode()) return
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return
  if (Notification.permission !== 'granted') return
  await getAndRegisterToken()
}

// Registers the foreground onMessage handler. Call once on app boot (non-devMode).
// When the app is focused, Chrome suppresses system notifications — this shows a toast instead.
export function initOnMessage(): void {
  if (isDevMode()) return
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return
  isSupported().then(supported => {
    if (!supported) return
    const messaging = getMessaging(app)
    onMessage(messaging, payload => {
      const title = payload.notification?.title ?? 'Mombongo'
      const body = payload.notification?.body ?? ''
      toast(title, { description: body || undefined })
      window.dispatchEvent(new CustomEvent('mombongo:notification'))
    })
  }).catch(() => undefined)
}

// Must be called from a user gesture (button click).
// Prompts for permission, then registers the FCM token.
// Returns true if permission was granted.
export async function requestFcmPermission(): Promise<boolean> {
  if (isDevMode()) return false
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return false
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false
  await getAndRegisterToken()
  return true
}
